import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey =
  import.meta.env.VITE_SUPABASE_ANON_KEY || import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || '';

/**
 * No-op lock — bypass Supabase's cross-tab auth synchronization.
 *
 * History: we previously had a navigator.locks-based wrapper with a 1s
 * timeout fallback. Two production failures forced us to drop it:
 *
 * 1. OAuth PKCE callback path: _initialize() wraps the URL code-exchange
 *    in this lock and never releases it before _notifyAllSubscribers()
 *    fires the SIGNED_IN listener. Our listener then makes a REST query
 *    (e.g. supabase.from('users').select(...)), which tries to acquire
 *    the SAME lock for token-attachment — deadlock until the 1s timeout.
 *    Worse, every subsequent REST call repeats the dance, adding ~1s
 *    each. In private browsing the cumulative wait blew past our 5s
 *    syncSupabaseSession DB-select timeout, falling back to a minimal
 *    user record and re-triggering onboarding for existing users.
 * 2. Safari/iOS already had to skip the lock entirely (zombie locks in
 *    private mode + WKWebView inconsistencies), so multi-tab sync was
 *    already broken on a large slice of our traffic.
 *
 * Trade-off accepted: in the rare case a user has multiple tabs open
 * during a token refresh, both tabs may issue refresh requests in
 * parallel. Supabase handles 401-on-stale-token with its own retry, so
 * the worst observable outcome is one redundant network round-trip.
 */
async function noOpLock<R>(
  _name: string,
  _acquireTimeout: number,
  fn: () => Promise<R>
): Promise<R> {
  return fn();
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
    lock: noOpLock,
    storage: typeof window !== 'undefined' ? window.localStorage : undefined,
  },
});
